import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, UrlTree } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ModuleRegistryService } from '../shared/services/modules/module-registry.service';
import { ModuleScopeService } from '../shared/services/modules/module-scope.service';
import { PermissionService } from '../shared/services/permission/permission.service';
import { firstModulePath, menuUidForKey } from './launchpad-tiles';

/**
 * /app/:key — one URL per module (ZEY-2419). Nothing ever opens outside the shell: every module, Angular
 * or micro-UI, opens at its first screen under the shell's header, with this tab's sidebar narrowed to
 * that module. A micro-UI's menu paths are shell routes that frame it (/hr/…, /manufacturing/…).
 * Unknown, disabled, or not in the user's menu: back to the launchpad.
 */
export const appModuleGuard: CanActivateFn = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const registry = inject(ModuleRegistryService);
  const permissions = inject(PermissionService);
  const scope = inject(ModuleScopeService);
  const key = route.paramMap.get('key');

  return forkJoin([permissions.loadMenuItems(), registry.launchable()]).pipe(
    map(([menu, modules]) => {
      const uid = key ? menuUidForKey(key, modules)?.toLowerCase() : undefined;
      const node = uid ? menu.find(n => n.uid?.toLowerCase() === uid) : undefined;
      if (!node) return router.parseUrl('/');

      const path = firstModulePath(node);
      if (!path) return router.parseUrl('/');
      scope.set(node.uid);
      return router.parseUrl(path);
    })
  );
};
